/**
 * @file        distributive-session.js - DistributiveSession class dcp.identity module
 *
 * This is how we communicate a session key from the d9ve origin to a third-party origin:
 * - third party makes sessionId
 * - authenticate.html chooses identity
 * - authorize.html gets the user's blessing to proxy identity (and other KSs) with reduced privs
 * - authorize.html makes a keystore with those privs using new-px
 * - authorize.html stores that keystore with the sessionId in d9ve local storage (memoizeSession)
 * - third party loads get-session-memo.html in iframe with d9ve origin
 * - third party posts sessionId to get-session-memo.html
 * - get-session-memo.html posts full session, including new-px keystore, back to third party
 *
 * @author      Wes Garland
 * @date        Aug 2024
 */
'use strict';

const { KVIN }                = require('./index');
const { DCPError }            = require('dcp/common/dcp-error');
const { randStr21,
        randStrAlphabet,
      }                       = require('dcp/protocol/utils');
const { PrivateKey,
        Keystore,
        IdKeystore,
      }                       = require('dcp/dcp-client/wallet');
const { localStorage,
        sessionStorage,
      }                       = require('./ls-storage.js');
const { assert }              = require('dcp/common/dcp-assert');
const { build }               = require('dcp/common/dcp-build');

const isDebugBuild = build === 'debug';

class DistributiveSession
{
  #identity;
  #id;

  /**
   * Create a new session and its associated session id. Knowing the session id lets a foreign origin
   * request the proxy key associated with that session from the auth server origin. It does not allow
   * it to request the private key stored in the context. Note that while all sessions have sessionIds,
   * they do not necessarily have an identity.
   *
   * @param   {string} sessionId   [optional]  the session id for this session; not passing this
   *                                           a new one.
   * @returns {object} a new session object - does not become active unless explicitly activated
   */
  constructor(sessionId)
  {
    if (!sessionId)
      sessionId = 'ids-' + randStr21() + '-' + Date.now() + '-' + btoa(dcpConfig.pxAuth.location.origin);

    if (!DistributiveSession.validateSessionId(sessionId))
      throw new Error(`invalid session id: ${sessionId}`);

    this.#id = sessionId;
  }

  /**
   * Validate sessionId
   *
   * @returns true when the session id matches sidRE and encodes the origin of the pxAuth service in
   *               our active dcpConfig.
   */
  static validateSessionId(sessionId)
  {
    const sidRE = new RegExp(`^(?:ids-)([${randStrAlphabet}]{21})(?:-)([0-9]+)(?:-)([A-Za-z0-9_/=]+)$`);
    const m = sessionId && sessionId.match(sidRE);
    if (!m || m.length !== 4)
      return false;

    assert(sessionId.indexOf('+') === -1); /* ensure URL safe; randStr alpha ok, btoa rfc 4648s5 */
    return atob(m[3]) === dcpConfig.pxAuth.location.origin;
  }

  get id()
  {
    if (!this.#id)
      throw new DCPError('Cannot get Distributive Identity before login', 'DCPI-0001');
    return this.#id;
  }

  get identity()
  {
    return this.#identity;
  }

  set identity(ks)
  {
    if (this.#identity)
      throw new DCPError(`Session ${this.#id} already has identity ${this.#identity}`, 'DCPI-0002');
    assert(ks instanceof Keystore || ks instanceof PrivateKey);
    this.#identity = ks;
  }

  /**
   * Save the current state of this session into localStorage. Saving the session like this should make
   * possible for other pages that share the browser session (eg iframes, popups, tabs opened from this
   * tab) use this session.
   */
  save()
  {
    const snapshot = Object.assign({ id: this.id, identity: this.identity }, this);
    const serialized = KVIN.stringify(snapshot);
    sessionStorage.setItem('dcp-current-session-snapshot', serialized);
    if (this.persist)
      localStorage.setItem('dcp-current-session-snapshot', serialized);
  }

  /**
   * Memoize this session into sessionStorage on a d9ve origin, so that it can be located by the code in
   * get-session-memo.html. The memoization protocol is purposefully kept really light, storing almost
   * nothing and serializable with JSON, so that get-session-memo.html can be fast and small.
   *
   * @note *** This code MUST be kept synchronized with auth/www/docs/get-session.memo.html
   *
   * @param {string} origin        origin which must match caller of get-session-memo.html
   * @param {any}    persist       [optional] if truey, store the memo in localStorage, else store it in
   *                               sessionStorage.
   */
  async memoize(origin, persist)
  {
    assert(window.location.origin === dcpConfig.pxAuth.location.origin);
    assert(this.#identity);
    assert(origin);

    const sessionId = this.#id;
    const key = String(await this.#identity.getPrivateKey()); /* DCP-4423 needed */
    const props = Object.assign({}, this);

    function storeMemo(storageHnd)
    {
      const sessionDetailsLookup = JSON.parse(storageHnd.getItem('dcp-session-memos') || '{}');
      sessionDetailsLookup[sessionId] = { key, origin, props };
      if (persist)
        sessionDetailsLookup[sessionId].persist = true;
      storageHnd.setItem('dcp-session-memos', JSON.stringify(sessionDetailsLookup));
    }

    storeMemo(persist ? localStorage : sessionStorage);
  }

  restoreFromMemo(memo)
  {
    assert(memo.key);
    if (this.identity)
      throw new Error('cannot restore session which already has an identity');

    Object.assign(this, memo.props);
    const pk = new PrivateKey(memo.key);
    this.#identity = new IdKeystore(pk, ''); /* DCP-4423 needed */
  }

  /**
   * Load get-session-memo.html into a d9ve origin iframe. Then post our session id to the iframe,
   * and get the session memo back.
   */
  static a$getSessionMemo(memoSessionId)
  {
    return new Promise((resolve, reject) => {
      const iframe = document.createElement('IFRAME');
      iframe.setAttribute('src', dcpConfig.pxAuth.location.resolve('get-session-memo.html'));
      iframe.setAttribute('style', 'height: 0; border: 0; margin: 0; padding: 0; position: fixed; top: 0; left: 0;');
      window.addEventListener('message', ev => {
        var message = infParseJSONObject(ev.data);
        if (message?.type !== 'distributive-session-key')
          return; /* message not for us */

        if (message.fail)
        {
          console.debug('could not get session memo; ' + message.fail);
          return resolve(false);
        }
        return resolve(message.sessionMemo);
      });

      iframe.addEventListener('error', ev => { /* does not fire xss */
        console.error(`error loading ${iframe.getAttribute('src')}:`, ev)
      });

      iframe.addEventListener('load', ev => {
        iframe.contentWindow.postMessage(memoSessionId, dcpConfig.pxAuth.location.origin);
      });

      setTimeout(() => {
        console.error('load timeout for', iframe.getAttribute('src'));
        reject(new Error('get-session-memo load timeout'))
      }, isDebugBuild ? 3000 : 30000);

      if (document.body)
        document.body.appendChild(iframe);
      else
      {
        function drawIframe()
        {
          if (!document.body)
            return;
          document.body.appendChild(iframe);
          document.removeEventListener('readystatechange', drawIframe);
        }
        document.addEventListener('readystatechange', drawIframe);
      }
    });
  }
}

/**
 * Infallible JSON parse; like JSON.parse(), except it returns undefined when the JSON is invalid
 * or does not correspond to an object.
 */
function infParseJSONObject(str)
{
  if (str[0] !== '{')
    return str;
  try
  {
    return JSON.parse(str);
  }
  catch(error){} // eslint-disable-line no-empty
}

exports.DistributiveSession = DistributiveSession;
